/**
 * Skeleton Components - Loading Placeholders
 * 
 * Animated skeleton components with shimmer effect:
 * - SkeletonBox - Basic rectangular skeleton
 * - SkeletonCircle - Avatar/icon placeholders
 * - SkeletonText - Text line placeholders
 * - TournamentCardSkeleton - Full card loading state
 * - LeaderboardRowSkeleton - Ranking row placeholder
 * - MatchCardSkeleton - Match card placeholder
 * 
 * OPTIMIZED: Uses shared pulse animation instead of individual shimmer overlays
 */

import React, { memo, useEffect } from 'react';
import { View, StyleSheet, Dimensions } from 'react-native';
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    withRepeat,
    withSequence,
    withTiming,
} from 'react-native-reanimated';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// Colors
const SKELETON_COLORS = {
    base: 'rgba(45, 27, 78, 0.6)',
    highlight: 'rgba(168, 85, 247, 0.3)',
};

// ============================================
// SHARED SHIMMER HOOK (OPTIMIZED)
// ============================================

/**
 * Shared shimmer animation hook
 * Uses opacity-based pulse instead of translateX per component
 * All skeletons share the same animation loop for better performance
 */
const useShimmer = () => {
    const opacity = useSharedValue(0.4);

    useEffect(() => {
        opacity.value = withRepeat(
            withSequence(
                withTiming(0.8, { duration: 750 }),
                withTiming(0.4, { duration: 750 })
            ),
            -1,
            true
        );
    }, []);

    return useAnimatedStyle(() => ({
        opacity: opacity.value,
    }));
};

// ============================================
// BASIC SKELETON SHAPES
// ============================================

// Skeleton Box
export const SkeletonBox = memo(({
    width = '100%',
    height = 20,
    borderRadius = 8,
    style,
}) => {
    const shimmerStyle = useShimmer();

    return (
        <Animated.View style={[
            styles.skeletonBase,
            { width, height, borderRadius },
            style,
            shimmerStyle,
        ]} />
    );
});

// Skeleton Circle
export const SkeletonCircle = memo(({
    size = 40,
    style,
}) => {
    const shimmerStyle = useShimmer();

    return (
        <Animated.View style={[
            styles.skeletonBase,
            { width: size, height: size, borderRadius: size / 2 },
            style,
            shimmerStyle,
        ]} />
    );
});

// Skeleton Text Line
export const SkeletonText = memo(({
    width = '80%',
    height = 14,
    style,
}) => {
    const shimmerStyle = useShimmer();

    return (
        <Animated.View style={[
            styles.skeletonBase,
            { width, height, borderRadius: height / 2 },
            style,
            shimmerStyle,
        ]} />
    );
});

// ============================================
// TOURNAMENT SPECIFIC SKELETONS
// ============================================

// Tournament Card Skeleton
export const TournamentCardSkeleton = memo(() => (
    <View style={styles.tournamentCard}>
        {/* Header */}
        <View style={styles.cardHeader}>
            <SkeletonBox width={120} height={24} borderRadius={12} />
            <SkeletonBox width={80} height={28} borderRadius={14} />
        </View>

        {/* Status */}
        <View style={styles.cardStatus}>
            <SkeletonCircle size={40} />
            <View style={styles.cardStatusText}>
                <SkeletonText width={100} height={12} />
                <SkeletonText width={150} height={16} style={{ marginTop: 6 }} />
            </View>
        </View>

        {/* Progress */}
        <SkeletonBox width="100%" height={8} borderRadius={4} style={{ marginTop: 16 }} />

        {/* Stats */}
        <View style={styles.cardStats}>
            <SkeletonBox width={80} height={40} borderRadius={10} />
            <SkeletonBox width={80} height={40} borderRadius={10} />
            <SkeletonBox width={80} height={40} borderRadius={10} />
        </View>

        {/* Button */}
        <SkeletonBox width="100%" height={48} borderRadius={12} style={{ marginTop: 16 }} />
    </View>
));

// Leaderboard Row Skeleton
export const LeaderboardRowSkeleton = memo(({ index = 0 }) => (
    <Animated.View
        style={[styles.leaderboardRow, { opacity: 1 - (index * 0.1) }]}
    >
        {/* Rank */}
        <SkeletonBox width={35} height={24} borderRadius={6} />

        {/* Avatar */}
        <SkeletonCircle size={40} style={{ marginLeft: 12 }} />

        {/* Name & Stats */}
        <View style={styles.leaderboardInfo}>
            <SkeletonText width={120} height={14} />
            <SkeletonText width={80} height={10} style={{ marginTop: 4 }} />
        </View>

        {/* Score */}
        <View style={styles.leaderboardScore}>
            <SkeletonBox width={45} height={20} borderRadius={6} />
            <SkeletonText width={40} height={8} style={{ marginTop: 4 }} />
        </View>
    </Animated.View>
));

// Match Card Skeleton
export const MatchCardSkeleton = memo(() => (
    <View style={styles.matchCard}>
        {/* Status */}
        <SkeletonBox width={60} height={20} borderRadius={10} style={{ alignSelf: 'center' }} />

        {/* Players */}
        <View style={styles.matchPlayers}>
            {/* Player 1 */}
            <View style={styles.matchPlayer}>
                <SkeletonCircle size={50} />
                <SkeletonText width={80} height={12} style={{ marginTop: 8 }} />
                <SkeletonBox width={30} height={24} borderRadius={6} style={{ marginTop: 4 }} />
            </View>

            {/* VS */}
            <SkeletonCircle size={36} />

            {/* Player 2 */}
            <View style={styles.matchPlayer}>
                <SkeletonCircle size={50} />
                <SkeletonText width={80} height={12} style={{ marginTop: 8 }} />
                <SkeletonBox width={30} height={24} borderRadius={6} style={{ marginTop: 4 }} />
            </View>
        </View>
    </View>
));

// Bracket Round Skeleton
export const BracketRoundSkeleton = memo(({ matchCount = 4 }) => (
    <View style={styles.bracketRound}>
        <SkeletonBox width={100} height={20} borderRadius={10} style={{ marginBottom: 16 }} />
        {Array.from({ length: matchCount }).map((_, i) => (
            <View key={i} style={styles.bracketMatch}>
                <SkeletonBox width="100%" height={60} borderRadius={10} style={{ marginBottom: 8 }} />
            </View>
        ))}
    </View>
));

// Stats Card Skeleton
export const StatsCardSkeleton = memo(() => (
    <View style={styles.statsCard}>
        <SkeletonCircle size={48} />
        <SkeletonBox width={60} height={32} borderRadius={8} style={{ marginTop: 12 }} />
        <SkeletonText width={80} height={12} style={{ marginTop: 8 }} />
    </View>
));

// Champions List Skeleton
export const ChampionsListSkeleton = memo(({ count = 5 }) => (
    <View>
        {Array.from({ length: count }).map((_, i) => (
            <View key={i} style={styles.championRow}>
                <SkeletonBox width={40} height={40} borderRadius={8} />
                <View style={{ flex: 1, marginLeft: 12 }}>
                    <SkeletonText width="60%" height={14} />
                    <SkeletonText width="40%" height={10} style={{ marginTop: 6 }} />
                </View>
                <SkeletonBox width={60} height={24} borderRadius={6} />
            </View>
        ))}
    </View>
));

// Full Screen Loading Skeleton
export const TournamentHubSkeleton = memo(() => (
    <View style={styles.fullScreen}>
        {/* Header */}
        <View style={styles.header}>
            <SkeletonCircle size={40} />
            <SkeletonText width={150} height={20} />
            <SkeletonCircle size={40} />
        </View>

        {/* Main Card */}
        <TournamentCardSkeleton />

        {/* Timeline */}
        <View style={styles.timeline}>
            <SkeletonText width={120} height={16} style={{ marginBottom: 12 }} />
            <View style={styles.timelineItems}>
                {[1, 2, 3, 4].map(i => (
                    <SkeletonCircle key={i} size={50} />
                ))}
            </View>
        </View>

        {/* Buttons */}
        <SkeletonBox width={SCREEN_WIDTH - 32} height={48} borderRadius={12} style={{ marginTop: 20 }} />
        <SkeletonBox width={SCREEN_WIDTH - 32} height={48} borderRadius={12} style={{ marginTop: 12 }} />
    </View>
));

// ============================================
// STYLES
// ============================================

const styles = StyleSheet.create({
    // Base
    skeletonBase: {
        backgroundColor: SKELETON_COLORS.base,
    },

    // Tournament Card
    tournamentCard: {
        backgroundColor: 'rgba(45, 27, 78, 0.5)',
        borderRadius: 20,
        padding: 20,
        marginHorizontal: 16,
        borderWidth: 1,
        borderColor: 'rgba(168, 85, 247, 0.2)',
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    cardStatus: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    cardStatusText: {
        marginLeft: 12,
    },
    cardStats: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 16,
    },

    // Leaderboard Row
    leaderboardRow: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(45, 27, 78, 0.4)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 8,
    },
    leaderboardInfo: {
        flex: 1,
        marginLeft: 12,
    },
    leaderboardScore: {
        alignItems: 'flex-end',
    },

    // Match Card
    matchCard: {
        backgroundColor: 'rgba(45, 27, 78, 0.5)',
        borderRadius: 14,
        padding: 16,
        marginBottom: 12,
    },
    matchPlayers: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'center',
        marginTop: 16,
    },
    matchPlayer: {
        alignItems: 'center',
    },

    // Bracket Round
    bracketRound: {
        marginRight: 16,
        width: 150,
    },
    bracketMatch: {},

    // Stats Card
    statsCard: {
        backgroundColor: 'rgba(45, 27, 78, 0.6)',
        borderRadius: 14,
        padding: 16,
        alignItems: 'center',
        width: (SCREEN_WIDTH - 54) / 2,
    },

    // Champion Row
    championRow: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(45, 27, 78, 0.4)',
        borderRadius: 12,
        padding: 14,
        marginBottom: 10,
    },

    // Full Screen
    fullScreen: {
        flex: 1,
        paddingTop: 16,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        marginBottom: 20,
    },
    timeline: {
        marginTop: 20,
        paddingHorizontal: 16,
    },
    timelineItems: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
});

export default {
    SkeletonBox,
    SkeletonCircle,
    SkeletonText,
    TournamentCardSkeleton,
    LeaderboardRowSkeleton,
    MatchCardSkeleton,
    BracketRoundSkeleton,
    StatsCardSkeleton,
    ChampionsListSkeleton,
    TournamentHubSkeleton,
};
